import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GenericFormPage, GenericDetailPage } from '../../../components/generic';
import { LockIcon, UnlockIcon } from '../../../components/ui';
import { closeAccPeriod, openAccPeriod } from '../../../services/mutations';
import { ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { useAccPeriod, AccPeriod } from './useData';
import {
  moduleConfig,
  getDetailSections,
  getStatusBadges,
  getInitialFormData,
  mapItemToFormData,
  isPeriodLocked,
  formatPeriodStatus,
} from './config';

export { default as List } from './List';

const PeriodLockToggle: React.FC<{
  item: AccPeriod;
  mutate: () => void | Promise<unknown>;
  colors: ThemeColors;
}> = ({ item, mutate, colors }) => {
  const [busy, setBusy] = useState(false);
  const locked = isPeriodLocked(item);

  const toggle = async () => {
    setBusy(true);
    try {
      if (locked) await openAccPeriod(item.id);
      else await closeAccPeriod(item.id);
      toast.success(locked ? 'Period unlocked' : 'Period locked');
      await mutate();
    } catch {
      toast.error('Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={locked ? 'Unlock period' : 'Lock period'}
      aria-label={locked ? 'Unlock period' : 'Lock period'}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        locked
          ? 'text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 focus:ring-amber-500/50 dark:text-amber-200 dark:bg-amber-950/40 dark:border-amber-800 dark:hover:bg-amber-950/60'
          : cn(
              colors.text,
              'border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:ring-indigo-500/50',
            ),
        busy && 'opacity-60 pointer-events-none',
      )}
    >
      {locked ? <UnlockIcon className="h-4 w-4 shrink-0" /> : <LockIcon className="h-4 w-4 shrink-0" />}
      <span>{locked ? 'Unlock' : 'Lock'}</span>
    </button>
  );
};

export const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editId = id && id !== 'new' ? parseInt(id, 10) : NaN;
  const isEdit = Number.isInteger(editId);
  const { item, isLoading: loadingItem } = useAccPeriod(isEdit ? editId : 0);

  const config = useMemo(
    () => ({ ...moduleConfig, getInitialFormData, mapItemToFormData }),
    [],
  );

  if (isEdit && loadingItem) {
    return (
      <div className="flex items-center justify-center flex-1 min-h-[240px] text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  if (isEdit && item && isPeriodLocked(item)) {
    return (
      <div className="max-w-3xl mx-auto w-full px-4 py-8">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Period {item.year} is <strong>{formatPeriodStatus(item.status)}</strong>. Unlock it from the{' '}
          <a href={moduleConfig.module.route.detail(item.id)} className="underline font-medium">
            detail page
          </a>{' '}
          before editing.
        </div>
      </div>
    );
  }

  return <GenericFormPage<AccPeriod> useData={useAccPeriod} config={config} />;
};

export const Detail: React.FC = () => {
  const config = useMemo(
    () => ({
      ...moduleConfig,
      getDetailSections,
      getStatusBadges,
      detail: {
        ...moduleConfig.detail,
        canEditItem: (item: AccPeriod) => !isPeriodLocked(item),
      },
      getDetailHeaderActions: (item: AccPeriod, mutate: () => void | Promise<unknown>, colors: ThemeColors) => (
        <PeriodLockToggle item={item} mutate={mutate} colors={colors} />
      ),
    }),
    [],
  );

  return <GenericDetailPage<AccPeriod> useData={useAccPeriod} config={config} />;
};
